import React, { useState, useEffect } from "react";
import { GrClose } from "react-icons/gr";
import { toast } from "react-toastify";
import Api from "../../Api/api";
import { ClipLoader } from "react-spinners";
import { useStateValue } from "../../states/StateProvider";
import { AES, enc } from "crypto-js";

const ConsultantSelectionModal = ({
    isOpen,
    onClose,
    onConsultantSelected,
    fromDate,
    toDate,
    taskId,
    portal,
    consultantName = null,
    editMode = false,
    existingConsultantId = null
}) => {
    const [availableConsultants, setAvailableConsultants] = useState([]);
    const [selectedConsultant, setSelectedConsultant] = useState("");
    const [loading, setLoading] = useState(false);
    const [consultantsLoaded, setConsultantsLoaded] = useState(false);
    const [blocking, setBlocking] = useState(false);

    // Date input states
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Get user information for API calls
    const [{ secretKey }] = useStateValue();
    const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
    const user = decryptedBytes ? JSON.parse(decryptedBytes.toString(enc.Utf8)) : null;

    useEffect(() => {
        if (isOpen) {
            // Format dates for input fields (YYYY-MM-DD)
            const formatDateForInput = (date) => {
                if (!date) return "";
                // Ensure date is a Date object
                const dateObj = date instanceof Date ? date : new Date(date);
                // Check if date is valid
                if (isNaN(dateObj.getTime())) return "";
                return dateObj.toISOString().split('T')[0];
            };

            // Pre-populate dates from FP task
            if (fromDate && toDate) {
                const formattedStartDate = formatDateForInput(fromDate);
                const formattedEndDate = formatDateForInput(toDate);

                console.log('Pre-populating dates in modal:', {
                    fromDate,
                    toDate,
                    formattedStartDate,
                    formattedEndDate
                });

                setStartDate(formattedStartDate);
                setEndDate(formattedEndDate);
            }

            // Reset consultant selection state when modal opens
            // Unless we're in edit mode with a pre-selected consultant
            if (editMode && consultantName) {
                // In edit mode, pre-populate the consultant name
                setSelectedConsultant(consultantName);
                setConsultantsLoaded(true); // Skip the "search" step
                console.log('Edit mode: Pre-populated consultant:', consultantName);
            } else {
                setConsultantsLoaded(false);
                setAvailableConsultants([]);
                setSelectedConsultant("");
            }
        }
    }, [isOpen, fromDate, toDate, editMode, consultantName]);

    const fetchAvailableConsultants = async () => {
        try {
            // Validate dates
            if (!startDate || !endDate) {
                toast.warning('Bitte geben Sie Start- und Enddatum ein');
                return;
            }

            if (new Date(startDate) > new Date(endDate)) {
                toast.warning('Startdatum darf nicht nach dem Enddatum liegen');
                return;
            }

            setLoading(true);

            const formData = new FormData();
            formData.append('fromDate', startDate);
            formData.append('toDate', endDate);
            formData.append('portal', portal);

            const response = await Api().post('sp_getAvailableConsultants', formData);

            console.log('sp_getAvailableConsultants response:', response.data);

            if (response.data && Array.isArray(response.data)) {
                // Log the structure of the first consultant to debug
                if (response.data.length > 0) {
                    console.log('First consultant structure:', response.data[0]);
                    console.log('First consultant keys:', Object.keys(response.data[0]));
                }

                setAvailableConsultants(response.data);
                setSelectedConsultant(""); // Reset selection
                setConsultantsLoaded(true);

                if (response.data.length === 0) {
                    toast.info('Keine Berater für den angegebenen Zeitraum verfügbar');
                }
            } else {
                setAvailableConsultants([]);
                setConsultantsLoaded(true);
                toast.warning('Keine verfügbaren Berater gefunden');
            }
        } catch (error) {
            console.error(error);
            toast.error("Fehler beim Laden der verfügbaren Berater.");
            setAvailableConsultants([]);
            setConsultantsLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const handleAddConsultant = async () => {
        if (!selectedConsultant) {
            toast.warning('Bitte wählen Sie einen Berater aus');
            return;
        }

        if (!user || !user.email) {
            toast.error('Benutzerinformationen nicht gefunden');
            return;
        }

        let consultant = null;

        // In edit mode, extract consultant data from existingConsultantId
        if (editMode && existingConsultantId) {
            console.log('Edit mode: Extracting consultant data from ID:', existingConsultantId);

            // Extract partnernr from ID format: "867837_consultant_188"
            const parts = existingConsultantId.toString().split('_consultant_');
            const partnernr = parts.length > 1 ? parts[1] : null;

            if (!partnernr) {
                toast.error('Konnte Berater-Nummer nicht aus ID extrahieren');
                return;
            }

            // Fetch consultant email from backend using partnernr
            try {
                const formData = new FormData();
                formData.append('fromDate', startDate);
                formData.append('toDate', endDate);
                formData.append('portal', portal);

                const consultantsResponse = await Api().post('sp_getAvailableConsultants', formData);

                // Find the consultant with matching partnernr
                const foundConsultant = consultantsResponse.data?.find(c =>
                    String(c.partnernr) === String(partnernr)
                );

                if (foundConsultant && foundConsultant.email) {
                    consultant = {
                        partnernr: partnernr,
                        fullname: consultantName || selectedConsultant,
                        email: foundConsultant.email,
                        name: consultantName || selectedConsultant
                    };
                    console.log('Edit mode: Found consultant email from API:', foundConsultant.email);
                } else {
                    console.warn('Edit mode: Could not find consultant email, using partnernr only');
                    consultant = {
                        partnernr: partnernr,
                        fullname: consultantName || selectedConsultant,
                        email: '', // Backend should handle partnernr lookup
                        name: consultantName || selectedConsultant
                    };
                }
            } catch (error) {
                console.error('Error fetching consultant email:', error);
                consultant = {
                    partnernr: partnernr,
                    fullname: consultantName || selectedConsultant,
                    email: '', // Backend should handle partnernr lookup
                    name: consultantName || selectedConsultant
                };
            }

            console.log('Edit mode: Created consultant object:', consultant);
        } else {
            // Add mode: Find consultant in availableConsultants array
            consultant = availableConsultants.find(c =>
                (c.partnernr && String(c.partnernr) === selectedConsultant) ||
                String(availableConsultants.indexOf(c)) === selectedConsultant
            );

            if (!consultant) {
                console.error('Consultant not found in availableConsultants:', { selectedConsultant, availableConsultants });
                toast.error('Ausgewählter Berater konnte nicht gefunden werden');
                return;
            }

            if (!consultant.email) {
                console.error('Consultant email is missing:', consultant);
                toast.error('Berater-E-Mail fehlt. Bitte versuchen Sie es erneut.');
                return;
            }
        }

        console.log('Adding consultant:', {
            partnernr: consultant.partnernr,
            fullname: consultant.fullname,
            email: consultant.email,
            fromDate: startDate,
            toDate: endDate,
            taskId: taskId
        });

        setBlocking(true);
        try {
            // Call sp_blockDate API
            const formData = new FormData();

            // In edit mode, prioritize partnernr for backend to lookup consultant
            if (editMode && consultant.partnernr) {
                formData.append('partnernr', consultant.partnernr);
                formData.append('UserEmail', consultant.email || ''); // Optional in edit mode
                console.log('Edit mode: Sending partnernr to backend:', consultant.partnernr);
            } else {
                // Add mode: Send consultant email as usual
                formData.append('UserEmail', consultant.email);
            }

            formData.append('fromDate', startDate);
            formData.append('toDate', endDate);
            formData.append('FP_ID', taskId);
            formData.append('planer', user.email);

            console.log('Calling sp_blockDate with params:', {
                partnernr: consultant.partnernr,
                UserEmail: consultant.email,
                fromDate: startDate,
                toDate: endDate,
                FP_ID: taskId,
                planer: user.email,
                editMode: editMode
            });

            const response = await Api().post('sp_blockDate', formData);

                // Check response for success message
                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    const message = response.data[0][''] || response.data[0].message;
                    if (message && message.includes('erstellt')) {
                        // Success case
                        const selectedFromDate = new Date(startDate);
                        const selectedToDate = new Date(endDate);

                        // Create consultant object with new structure
                        const consultantData = {
                            partnernr: consultant.partnernr,
                            fullname: consultant.fullname,
                            email: consultant.email,
                            id: consultant.partnernr,
                            name: consultant.fullname
                        };

                        onConsultantSelected(consultantData, selectedFromDate, selectedToDate);
                        onClose();
                        // Success toast is shown by the parent component after reloading Gantt data
                    } else {
                        throw new Error(message || 'Unerwartete Antwort vom Server');
                    }
                } else {
                    throw new Error('Keine gültige Antwort vom Server erhalten');
                }

        } catch (error) {
            console.error('Error blocking date:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Hinzufügen des Beraters zum Projekt';
            toast.error(errorMessage);
        } finally {
            setBlocking(false);
        }
    };

    const getConsultantName = (consultant) => {
        return consultant.name || 
               (consultant.vorname && consultant.nachname ? `${consultant.vorname} ${consultant.nachname}` : '') ||
               consultant.berater_name || 
               `Berater ${consultant.id}`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-text bg-opacity-50">
            <div className="relative w-full max-w-lg bg-white p-6 rounded-lg shadow-lg mx-4">
                <GrClose
                    className="cursor-pointer absolute top-4 right-4"
                    size="22px"
                    onClick={onClose}
                />

                <h2 className="text-xl font-bold mb-4">
                    {editMode ? 'Berater bearbeiten' : 'Berater hinzufügen'}
                </h2>

                {/* Show consultant name in edit mode */}
                {editMode && consultantName && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                        <label className="block font-medium mb-1 text-sm text-gray-600">
                            Berater
                        </label>
                        <div className="font-semibold text-mainBlue">
                            {consultantName}
                        </div>
                    </div>
                )}

                {/* Date Input Fields */}
                <div className="mb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block font-medium mb-1">
                                Startdatum
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full p-2 border rounded"
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">
                                Enddatum
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full p-2 border rounded"
                            />
                        </div>
                    </div>
                </div>

                {/* Search Button - only show in add mode */}
                {!editMode && (
                    <div className="mb-4">
                        <button
                            onClick={fetchAvailableConsultants}
                            disabled={loading}
                            className={`w-full py-2 px-4 rounded text-white ${
                                loading
                                    ? "bg-mainBlue opacity-50 cursor-not-allowed"
                                    : "bg-mainBlue hover:bg-blue-600"
                            }`}
                        >
                            {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <ClipLoader size={16} color={"#ffffff"} />
                                    <span>Berater suchen...</span>
                                </div>
                            ) : (
                                "Berater finden"
                            )}
                        </button>
                    </div>
                )}

                {/* Consultant Selection Dropdown - only show in add mode */}
                {consultantsLoaded && !editMode && (
                    <div className="mb-4">
                        <label className="block font-medium mb-1">
                            Verfügbare Berater auswählen
                        </label>

                        {availableConsultants.length > 0 ? (
                            <>
                                <style>
                                    {`
                                        .consultant-option-voll {
                                            background-color: #E2EFDA !important;
                                            color: #282828;
                                        }
                                        .consultant-option-teilweise {
                                            background-color: #fcf3d3 !important;
                                            color: #282828;
                                        }
                                        .consultant-option-cancel {
                                            background-color: #F8696B !important;
                                            color: white;
                                        }
                                        .consultant-select option:checked {
                                            font-weight: bold;
                                        }
                                    `}
                                </style>
                                <select
                                    value={selectedConsultant}
                                    onChange={(e) => setSelectedConsultant(e.target.value)}
                                    className="w-full p-2 border rounded consultant-select"
                                >
                                    <option value="">Bitte wählen...</option>
                                    {availableConsultants.map((consultant, index) => {
                                        const availability = consultant.Verfuegbarkeit?.toLowerCase();
                                        let optionClass = '';
                                        let availabilityLabel = '';

                                        if (availability === 'voll') {
                                            optionClass = 'consultant-option-voll';
                                            availabilityLabel = ' (voll verfügbar)';
                                        } else if (availability === 'teilweise') {
                                            optionClass = 'consultant-option-teilweise';
                                            availabilityLabel = ' (teilweise verfügbar)';
                                        } else {
                                            optionClass = 'consultant-option-cancel';
                                            availabilityLabel = ' (nicht verfügbar)';
                                        }

                                        return (
                                            <option
                                                key={consultant.partnernr || index}
                                                value={consultant.partnernr || index}
                                                className={optionClass}
                                            >
                                                {consultant.fullname}{availabilityLabel}
                                            </option>
                                        );
                                    })}
                                </select>
                            </>
                        ) : (
                            <p className="text-gray-500">
                                Keine Berater für den angegebenen Zeitraum verfügbar
                            </p>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                {(consultantsLoaded || editMode) && (availableConsultants.length > 0 || editMode) && (
                    <div className="flex gap-2">
                        <button
                            onClick={handleAddConsultant}
                            disabled={!selectedConsultant || blocking}
                            className={`flex-1 py-2 px-4 rounded text-white ${
                                !selectedConsultant || blocking
                                    ? "bg-mainBlue opacity-50 cursor-not-allowed"
                                    : "bg-mainBlue hover:bg-blue-600"
                            }`}
                        >
                            {blocking ? (
                                <div className="flex items-center justify-center gap-2">
                                    <ClipLoader size={16} color={"#ffffff"} />
                                    <span>{editMode ? 'Aktualisieren...' : 'Hinzufügen...'}</span>
                                </div>
                            ) : (
                                editMode ? "Berater aktualisieren" : "Berater hinzufügen"
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Abbrechen
                        </button>
                    </div>
                )}

                {/* Close Button if no consultants loaded yet and not in edit mode */}
                {!consultantsLoaded && !editMode && (
                    <div className="flex justify-end">
                        <button
                            onClick={onClose}
                            className="py-2 px-4 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Schließen
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConsultantSelectionModal;